import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Activity,
  AlertTriangle,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Factory,
  RefreshCw,
  UserRound,
  Users,
  Wrench,
} from "lucide-react";

import { Link } from "react-router-dom";

import { gmao } from "@/api";


const OPEN_STATUSES = {
  planned: "Planifiée",
  confirmed: "Confirmée",
  in_progress: "En cours",
};


function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}


function safeNumber(value) {
  const parsed = Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
}


function formatTime(value) {
  if (!value) {
    return "--:--";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "--:--";
  }

  return new Intl.DateTimeFormat(
    "fr-FR",
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(parsed);
}


function ClickableCard({
  to,
  children,
  className = "",
  testId,
}) {
  return (
    <Link
      to={to}
      className={`
        group
        block
        min-h-11
        rounded-2xl
        border
        border-[var(--do-border)]
        bg-[var(--do-bg-solid)]
        text-[var(--do-text)]
        transition
        hover:-translate-y-0.5
        hover:border-[var(--do-accent)]
        hover:shadow-md
        focus:outline-none
        focus-visible:ring-2
        focus-visible:ring-[var(--do-accent)]
        focus-visible:ring-offset-2
        ${className}
      `}
      data-testid={testId}
    >
      {children}
    </Link>
  );
}


function KpiCard({
  icon: Icon,
  label,
  value,
  to,
  tone = "default",
  testId,
}) {
  const toneClass =
    tone === "danger"
      ? "text-[var(--do-danger)]"
      : "text-[var(--do-accent)]";

  return (
    <ClickableCard
      to={to}
      className="p-4"
      testId={testId}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 text-xs font-semibold uppercase tracking-wide text-[var(--do-muted)]">
          {label}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Icon
            size={18}
            className={toneClass}
          />

          <ChevronRight
            size={16}
            className="text-[var(--do-muted)] transition-transform group-hover:translate-x-0.5"
          />
        </div>
      </div>

      <div
        className={`
          mt-3
          text-3xl
          font-black
          ${
            tone === "danger"
              ? "text-[var(--do-danger)]"
              : "text-[var(--do-text)]"
          }
        `}
      >
        {value}
      </div>
    </ClickableCard>
  );
}


function SectionCard({
  icon: Icon,
  title,
  children,
  className = "",
  testId,
}) {
  return (
    <section
      className={`
        overflow-hidden
        rounded-2xl
        border
        border-[var(--do-border)]
        bg-[var(--do-bg-solid)]
        ${className}
      `}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 border-b border-[var(--do-border)] px-4 py-4">
        <Icon
          size={18}
          className="text-[var(--do-accent)]"
        />

        <h2 className="font-black text-[var(--do-text)]">
          {title}
        </h2>
      </div>

      {children}
    </section>
  );
}


function EmptyState({
  children,
}) {
  return (
    <div className="px-5 py-10 text-center text-sm text-[var(--do-muted)]">
      {children}
    </div>
  );
}


function PlanningCard({
  intervention,
}) {
  return (
    <ClickableCard
      to={`/app/gmao/interventions/${encodeURIComponent(
        intervention.id
      )}`}
      className="rounded-xl p-4"
      testId={`gmao-dashboard-intervention-${intervention.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-[52px] text-sm font-black text-[var(--do-accent)]">
          {intervention.scheduledDate && !intervention.scheduledStart
            ? "Sans horaire"
            : formatTime(intervention.scheduledStart)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="truncate font-bold text-[var(--do-text)]">
            {intervention.title ||
              "Intervention"}
          </div>

          <div className="mt-1 text-sm text-[var(--do-muted)]">
            {intervention.machineName ||
              "Machine"}
            {intervention.siteName
              ? ` · ${intervention.siteName}`
              : ""}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--do-muted)]">
            <span>
              {intervention.assigneeName || intervention.technicianName ||
                "Non affecté"}
            </span>

            <span>
              {OPEN_STATUSES[
                intervention.status
              ] ||
                intervention.status ||
                "À planifier"}
            </span>
          </div>
        </div>

        <ChevronRight
          size={17}
          className="mt-1 shrink-0 text-[var(--do-muted)] transition-transform group-hover:translate-x-0.5"
        />
      </div>
    </ClickableCard>
  );
}


function SummaryCard({
  icon: Icon,
  label,
  value,
  to,
  danger = false,
  testId,
}) {
  return (
    <ClickableCard
      to={to}
      className="rounded-xl p-4"
      testId={testId}
    >
      <div className="flex items-center gap-3">
        <div
          className={`
            flex
            h-9
            w-9
            shrink-0
            items-center
            justify-center
            rounded-full
            ${
              danger
                ? "bg-[var(--do-danger-soft)] text-[var(--do-danger)]"
                : "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
            }
          `}
        >
          <Icon size={18} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-[var(--do-muted)]">
            {label}
          </div>

          <div
            className={`
              mt-1
              text-2xl
              font-black
              ${
                danger
                  ? "text-[var(--do-danger)]"
                  : "text-[var(--do-text)]"
              }
            `}
          >
            {value}
          </div>
        </div>

        <ChevronRight
          size={16}
          className="shrink-0 text-[var(--do-muted)] transition-transform group-hover:translate-x-0.5"
        />
      </div>
    </ClickableCard>
  );
}


function TechnicianCard({
  technician,
}) {
  const technicianId =
    technician.technicianId;

  return (
    <ClickableCard
      to={
        `/app/gmao/v2/planning?technician_id=${encodeURIComponent(
          technicianId
        )}&scope=open`
      }
      className="overflow-hidden"
      testId={`gmao-dashboard-technician-${technicianId}`}
    >
      <div className="flex items-center gap-3 border-b border-[var(--do-border)] p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--do-bg-2)] text-[var(--do-muted)]">
          <UserRound size={18} />
        </div>

        <div className="min-w-0 flex-1 truncate font-black">
          {technician.technicianName ||
            "Technicien"}
        </div>

        <ChevronRight
          size={16}
          className="shrink-0 text-[var(--do-muted)] transition-transform group-hover:translate-x-0.5"
        />
      </div>

      <div className="grid grid-cols-2 divide-x divide-y divide-[var(--do-border)] sm:grid-cols-4 sm:divide-y-0">
        <TechnicianMetric
          label="Aujourd’hui"
          value={technician.today}
        />

        <TechnicianMetric
          label="En cours"
          value={technician.inProgress}
        />

        <TechnicianMetric
          label="Ouvertes"
          value={technician.totalOpen}
        />

        <TechnicianMetric
          label="En retard"
          value={technician.overdue}
          danger={
            safeNumber(
              technician.overdue
            ) > 0
          }
        />
      </div>
    </ClickableCard>
  );
}


function TechnicianMetric({
  label,
  value,
  danger = false,
}) {
  return (
    <div className="min-w-0 px-3 py-3 text-center">
      <div className="truncate text-[10px] font-semibold text-[var(--do-muted)]">
        {label}
      </div>

      <div
        className={`
          mt-1
          text-xl
          font-black
          ${
            danger
              ? "text-[var(--do-danger)]"
              : "text-[var(--do-text)]"
          }
        `}
      >
        {safeNumber(value)}
      </div>
    </div>
  );
}


export default function GmaoDashboardV2() {
  const [
    dashboard,
    setDashboard,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");


  const load = useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const response =
          await gmao.getDashboard({
            timezone_name:
              Intl.DateTimeFormat()
                .resolvedOptions()
                .timeZone ||
              "Europe/Paris",
          });

        setDashboard(
          response || {}
        );
      } catch (requestError) {
        console.error(
          "GMAO dashboard error",
          requestError
        );

        setError(
          "Impossible de charger le dashboard GMAO."
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );


  useEffect(() => {
    load();
  }, [load]);


  const metrics =
    dashboard?.metrics || {};

  const todayInterventions =
    safeArray(
      dashboard?.todayInterventions
    );

  const technicianWorkload =
    safeArray(
      dashboard?.technicianWorkload
    );


  const teamTotalOpen =
    useMemo(
      () =>
        technicianWorkload.reduce(
          (
            total,
            technician
          ) =>
            total +
            safeNumber(
              technician.totalOpen
            ),
          0
        ),
      [technicianWorkload]
    );


  const canSeeMaintenanceSummary =
    [
      "admin",
      "maintenance_manager",
    ].includes(
      dashboard?.role
    );


  if (loading) {
    return (
      <div className="acta-page-gutters">
        <div className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-8 text-center text-sm text-[var(--do-muted)]">
          Chargement du dashboard GMAO...
        </div>
      </div>
    );
  }


  if (
    error &&
    !dashboard
  ) {
    return (
      <div className="acta-page-gutters">
        <div className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-8 text-center">
          <AlertTriangle
            size={24}
            className="mx-auto text-[var(--do-danger)]"
          />

          <div className="mt-3 font-bold text-[var(--do-text)]">
            {error}
          </div>

          <button
            type="button"
            onClick={() => load()}
            className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-2 text-sm font-bold text-[var(--do-text)] transition hover:border-[var(--do-accent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--do-accent)]"
          >
            <RefreshCw size={16} />
            Réessayer
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="acta-page-gutters">
      {error ? (
        <div className="mb-4 rounded-xl border border-[var(--do-danger)] bg-[var(--do-danger-soft)] px-4 py-3 text-sm text-[var(--do-danger)]">
          {error}
        </div>
      ) : null}

      <section
        className="grid grid-cols-2 gap-3 lg:grid-cols-5"
        data-testid="gmao-dashboard-v2-kpis"
      >
        <KpiCard
          icon={Factory}
          label="Machines installées"
          value={safeNumber(
            metrics.machineInstalledCount
          )}
          to="/app/gmao/v2/parc?location_status=installed"
          testId="gmao-v2-kpi-machines-installed"
        />

        <KpiCard
          icon={CalendarDays}
          label="Aujourd’hui"
          value={safeNumber(
            metrics.todayCount
          )}
          to="/app/gmao/v2/planning"
          testId="gmao-v2-kpi-today"
        />

        <KpiCard
          icon={AlertTriangle}
          label="En retard"
          value={safeNumber(
            metrics.overdueCount
          )}
          to="/app/gmao/v2/planning?scope=open"
          tone={
            safeNumber(
              metrics.overdueCount
            ) > 0
              ? "danger"
              : "default"
          }
          testId="gmao-v2-kpi-overdue"
        />

        <KpiCard
          icon={ClipboardList}
          label="Tickets SAV ouverts"
          value={safeNumber(
            metrics.savOpenTicketCount
          )}
          to="/app/gmao/sav?scope=open"
          testId="gmao-v2-kpi-sav"
        />

        <KpiCard
          icon={Wrench}
          label="Maintenances à venir"
          value={safeNumber(
            metrics.maintenanceDueSoonCount
          )}
          to="/app/gmao/maintenance"
          className="col-span-2 lg:col-span-1"
          testId="gmao-v2-kpi-maintenance"
        />
      </section>

      <section
        className={`
          mt-6
          grid
          gap-6
          ${
            canSeeMaintenanceSummary
              ? "lg:grid-cols-2"
              : "grid-cols-1"
          }
        `}
      >
        <SectionCard
          icon={CalendarDays}
          title="Planning du jour"
          testId="gmao-dashboard-v2-today"
        >
          {todayInterventions.length ? (
            <div className="space-y-3 p-4">
              {todayInterventions.map(
                (intervention) => (
                  <PlanningCard
                    key={
                      intervention.id
                    }
                    intervention={
                      intervention
                    }
                  />
                )
              )}
            </div>
          ) : (
            <EmptyState>
              Aucune intervention planifiée aujourd’hui.
            </EmptyState>
          )}
        </SectionCard>

        {canSeeMaintenanceSummary ? (
          <SectionCard
            icon={Activity}
            title="Synthèse maintenance"
            testId="gmao-dashboard-v2-maintenance-summary"
          >
            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
              <SummaryCard
                icon={ClipboardList}
                label="Charge totale"
                value={teamTotalOpen}
                to="/app/gmao/v2/planning?scope=open"
                testId="gmao-v2-summary-total"
              />

              <SummaryCard
                icon={Users}
                label="Non affectées"
                value={safeNumber(
                  metrics.unassignedOpenCount
                )}
                to="/app/gmao/v2/planning?technician_id=unassigned&scope=open"
                testId="gmao-v2-summary-unassigned"
              />

              <SummaryCard
                icon={ClipboardList}
                label="SAV non affecté"
                value={safeNumber(
                  metrics.savUnassignedOpenTicketCount
                )}
                to="/app/gmao/sav?assignment=unassigned&scope=open"
                testId="gmao-v2-summary-sav-unassigned"
              />

              <SummaryCard
                icon={AlertTriangle}
                label="Maintenances en retard"
                value={safeNumber(
                  metrics.maintenanceDueCount
                )}
                to="/app/gmao/maintenance"
                danger={
                  safeNumber(
                    metrics.maintenanceDueCount
                  ) > 0
                }
                testId="gmao-v2-summary-maintenance-overdue"
              />
            </div>
          </SectionCard>
        ) : null}
      </section>

      <SectionCard
        icon={Users}
        title={
          dashboard?.role ===
          "technician"
            ? "Ma charge"
            : "Charge des techniciens"
        }
        className="mt-6"
        testId="gmao-dashboard-v2-workload"
      >
        {technicianWorkload.length ? (
          <div className="grid gap-3 p-4 xl:grid-cols-3">
            {technicianWorkload.map(
              (technician) => (
                <TechnicianCard
                  key={
                    technician.technicianId
                  }
                  technician={
                    technician
                  }
                />
              )
            )}
          </div>
        ) : (
          <EmptyState>
            Aucune charge technicien à afficher.
          </EmptyState>
        )}
      </SectionCard>
    </div>
  );
}
